import { supabase } from './supabase';
import { checkRateLimit } from './rate-limit';

interface AnalysisRequest {
  url: string;
  userId: string;
  email: string;
  timestamp: string;
  repositoryName?: string;
  owner?: string;
}

function parseGitHubUrl(url: string) {
  try {
    const parsedUrl = new URL(url);
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    
    if (pathParts.length >= 2) {
      return {
        owner: pathParts[0],
        repositoryName: pathParts[1],
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function validateGitHubUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    const isGitHub = parsedUrl.hostname === 'github.com';
    const hasValidPath = parsedUrl.pathname.split('/').filter(Boolean).length >= 2;
    return isGitHub && parsedUrl.protocol === 'https:' && hasValidPath;
  } catch {
    return false;
  }
}

export async function analyzeRepository(url: string, userId: string) {
  try {
    // Check rate limit before proceeding
    const { canProceed, timeLeft } = await checkRateLimit(userId);
    if (!canProceed) {
      throw new Error(`Please wait ${Math.ceil(timeLeft)} seconds before submitting another analysis`);
    }

    const webhookUrl = import.meta.env.VITE_MAKE_WEBHOOK_URL;
    if (!webhookUrl) {
      throw new Error('Webhook URL not configured');
    }

    const repoInfo = parseGitHubUrl(url);
    if (!repoInfo) {
      throw new Error('Invalid GitHub repository URL');
    }

    // Get user's email
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    // First, save to Supabase
    const { data: analysis, error: dbError } = await supabase
      .from('repository_analyses')
      .insert([{ 
        url, 
        user_id: userId,
        email: user.email,
        status: 'pending',
        repository_name: repoInfo.repositoryName,
        owner: repoInfo.owner
      }])
      .select()
      .single();

    if (dbError) throw dbError;

    // Then send to Make.com
    const analysisRequest: AnalysisRequest = {
      url,
      userId,
      email: user.email,
      timestamp: new Date().toISOString(),
      repositoryName: repoInfo.repositoryName,
      owner: repoInfo.owner
    };

    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(analysisRequest),
    });

    if (!response.ok) {
      throw new Error(`Failed to send repository for analysis: ${response.statusText}`);
    }

    return { data: analysis, error: null };
  } catch (error) {
    console.error('Analysis error:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}